// The same private link for the same file, for as long as it is good — so the browser can recognise a picture it already has.
//
// ⚠⚠ THE FAULT THIS FIXES (measured 2026-09-17, after "it is taking forever to load pages"). Every file lives in a private
// bucket and is shown through a SIGNED link. The app signed a FRESH link on every page load — and a fresh link is a new
// address, so the browser's cache never matched anything: the 2.6 MB map, every token's face and every thumbnail (110 files on
// the campaign home) were downloaded again, in full, on every load and every reload. Measured: 0 of 29 pictures from cache.
// On a day when Supabase's gateway was degraded, one of those downloads took 29 seconds.
//
// THE FIX, in one place: `supabase.storage.from(bucket)` is wrapped (lib/supabase.ts), and a signed link is REMEMBERED —
// in memory and in localStorage — until it is close to expiring. Same file, same link, so the browser serves it from its
// own cache (the files carry max-age=3600; after that it re-checks with the server, which is a few bytes, not the file).
// Nothing else changes: the callers still ask for a signed link and still get one that works.
//
// ⚠ WHAT IS KEPT, AND FOR HOW LONG. Only the link — never the file. A link is reused while at least REUSE_MARGIN of its life
// is left, so nothing handed out is about to die. Forgotten on sign-out and when a different account signs in; a link to a
// file that is removed, moved or uploaded over is forgotten at once. Bump VERSION to throw every remembered link away.
type Entry = { url: string; exp: number };

const VERSION = "v1";
const KEY = `vttmaps.signedUrls.${VERSION}`;
const OWNER_KEY = `${KEY}.owner`;
/** Never hand out a link with less than this left (or half its life, for a short one). */
const REUSE_MARGIN_MS = 45 * 60_000;
const MAX_ENTRIES = 1500;

let mem: Map<string, Entry> | null = null;
let saveTimer: ReturnType<typeof setTimeout> | null = null;

function load(): Map<string, Entry> {
  if (mem) return mem;
  mem = new Map();
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "{}") as Record<string, Entry>;
    const now = Date.now();
    for (const [k, e] of Object.entries(raw)) if (e && typeof e.url === "string" && typeof e.exp === "number" && e.exp > now) mem.set(k, e);
  } catch { /* nothing remembered: every link is simply signed afresh, as before */ }
  return mem;
}

function saveSoon(): void {
  if (saveTimer) return;
  saveTimer = setTimeout(() => {
    saveTimer = null;
    try {
      let entries = [...load()];
      if (entries.length > MAX_ENTRIES) entries = entries.sort((a, b) => b[1].exp - a[1].exp).slice(0, MAX_ENTRIES);
      localStorage.setItem(KEY, JSON.stringify(Object.fromEntries(entries)));
    } catch { /* a full or blocked store only costs the reuse */ }
  }, 400);
}

const keyOf = (bucket: string, path: string, variant = "") => `${bucket}|${path}|${variant}`;

/** A remembered link that still has enough life left, or null. */
export function recallSigned(bucket: string, path: string, ttlSeconds: number, variant = "", now = Date.now()): string | null {
  const e = load().get(keyOf(bucket, path, variant));
  if (!e) return null;
  const need = Math.min(REUSE_MARGIN_MS, (ttlSeconds * 1000) / 2);
  return e.exp - now >= need ? e.url : null;
}

export function rememberSigned(bucket: string, path: string, ttlSeconds: number, url: string, variant = "", now = Date.now()): void {
  load().set(keyOf(bucket, path, variant), { url, exp: now + ttlSeconds * 1000 });
  saveSoon();
}

/** The file at this path changed or went away: its link must not be served again. */
export function forgetSigned(bucket: string, path: string): void {
  const m = load();
  const head = `${bucket}|${path}|`;
  let hit = false;
  for (const k of [...m.keys()]) if (k.startsWith(head)) { m.delete(k); hit = true; }
  if (hit) saveSoon();
}

export function forgetAllSigned(): void {
  mem = new Map();
  try { localStorage.removeItem(KEY); } catch { /* fine */ }
}

/** The links belong to whoever signed them: a different account (or nobody) starts clean. */
export function signedLinksBelongTo(userId: string | null): void {
  try {
    const was = localStorage.getItem(OWNER_KEY);
    if (was !== null && was !== (userId ?? "")) forgetAllSigned();
    localStorage.setItem(OWNER_KEY, userId ?? "");
  } catch { /* no store: nothing was remembered either */ }
}

type SignedOne = { data: { signedUrl: string } | null; error: unknown };
type SignedMany = { data: { path: string | null; signedUrl: string; error: string | null }[] | null; error: unknown };
type BucketApi = {
  createSignedUrl: (path: string, ttl: number, options?: { download?: string | boolean; transform?: unknown }) => Promise<SignedOne>;
  createSignedUrls: (paths: string[], ttl: number, options?: { download?: string | boolean }) => Promise<SignedMany>;
};

/** Wrap one bucket's API: signing consults the memory first; anything that changes a file forgets its link. */
export function withSignedUrlMemory<T extends object>(bucket: string, api: T): T {
  const raw = api as unknown as BucketApi & Record<string, (...a: unknown[]) => unknown>;
  const variantOf = (o?: { download?: string | boolean; transform?: unknown }) => (o && (o.download || o.transform) ? JSON.stringify(o) : "");
  return new Proxy(api, {
    get(target, prop, receiver) {
      if (prop === "createSignedUrl") {
        return async (path: string, ttl: number, options?: { download?: string | boolean; transform?: unknown }): Promise<SignedOne> => {
          const variant = variantOf(options);
          const had = recallSigned(bucket, path, ttl, variant);
          if (had) return { data: { signedUrl: had }, error: null };
          const res = await raw.createSignedUrl.call(target, path, ttl, options);
          if (res.data?.signedUrl) rememberSigned(bucket, path, ttl, res.data.signedUrl, variant);
          return res;
        };
      }
      if (prop === "createSignedUrls") {
        return async (paths: string[], ttl: number, options?: { download?: string | boolean }): Promise<SignedMany> => {
          const variant = variantOf(options);
          const known = new Map<string, string>();
          for (const p of paths) { const u = recallSigned(bucket, p, ttl, variant); if (u) known.set(p, u); }
          const missing = paths.filter((p) => !known.has(p));
          let error: unknown = null;
          if (missing.length) {
            const res = await raw.createSignedUrls.call(target, missing, ttl, options);
            error = res.error;
            for (const d of res.data ?? []) if (d.signedUrl && d.path) { known.set(d.path, d.signedUrl); rememberSigned(bucket, d.path, ttl, d.signedUrl, variant); }
            // Every one failed and nothing was remembered: hand the failure back exactly as it came.
            if (!known.size) return res;
          }
          return { data: paths.map((p) => ({ path: p, signedUrl: known.get(p) ?? "", error: known.has(p) ? null : "Could not sign this path" })), error: known.size ? null : error };
        };
      }
      const value = Reflect.get(target, prop, receiver) as unknown;
      if (typeof value !== "function") return value;
      if (prop === "remove") return (paths: string[], ...rest: unknown[]) => { for (const p of paths ?? []) forgetSigned(bucket, p); return (value as (...a: unknown[]) => unknown).call(target, paths, ...rest); };
      if (prop === "upload" || prop === "update" || prop === "move" || prop === "copy" || prop === "uploadToSignedUrl") {
        return (path: string, ...rest: unknown[]) => { forgetSigned(bucket, path); if ((prop === "move" || prop === "copy") && typeof rest[0] === "string") forgetSigned(bucket, rest[0]); return (value as (...a: unknown[]) => unknown).call(target, path, ...rest); };
      }
      return (value as (...a: unknown[]) => unknown).bind(target);
    },
  });
}

// ── The same question asked twice in the same instant gets one answer.

type Pending = { at: number; p: Promise<Response> };
const inFlight = new Map<string, Pending>();
/** Two callers within this of each other asked at "the same moment". A re-read ON PURPOSE (after a realtime connect) comes later and is never merged. */
const SAME_MOMENT_MS = 120;

/**
 * ⚠ ONLY IDENTICAL READS, ONLY AT THE SAME MOMENT. On load, two parts of the page ask the database the very same thing in
 * the same tick (the scenes list, the doorways, the campaign's events — measured: four each where two would do). They now
 * share one request. A read that comes LATER is a different question — the realtime hooks re-read on SUBSCRIBED precisely
 * to catch what changed in between — and is always sent. Writes, RPCs and anything with an abort signal are never touched.
 */
export function sameMomentFetch(base: typeof fetch = fetch): typeof fetch {
  return (input, init) => {
    const method = (init?.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase();
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    // ⚠ A WRITE MAKES EVERY READ IN FLIGHT OLD NEWS: a read that starts after it must not be handed an answer from before it.
    if (method !== "GET" && method !== "HEAD") inFlight.clear();
    if (method !== "GET" || init?.signal || !url.includes("/rest/v1/") || url.includes("/rest/v1/rpc/")) return base(input, init);
    const h = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    const key = [url, h.get("authorization") ?? "", h.get("accept") ?? "", h.get("prefer") ?? "", h.get("range") ?? "", h.get("accept-profile") ?? ""].join("\n");
    const now = Date.now();
    const had = inFlight.get(key);
    if (had && now - had.at <= SAME_MOMENT_MS) return had.p.then((r) => r.clone());
    const p = base(input, init);
    const entry: Pending = { at: now, p };
    inFlight.set(key, entry);
    const done = () => { if (inFlight.get(key) === entry) inFlight.delete(key); };
    p.then(done, done);
    return p.then((r) => r.clone());
  };
}
